import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SortingState } from "@tanstack/react-table";
import { keepPreviousData } from "@tanstack/react-query";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Breadcrumbs } from "ui/breadcrumbs";
import ColumnOptions from "components/partials/column-options/column-options";
import ControlBar from "components/partials/control-bar/control-bar";
import { Button } from "ui/button";
import {
  DataGrid,
  TableLoader,
  FillHeightPage,
  TableContainer,
  TableFillHeightBody,
  TableSearchAndFilters,
  shouldPinPrimaryColumnLeft,
} from "ui/data-grid";
import CreateCampaignTemplateModal from "containers/admin/clients/client/campaign-templates-page/create-campaign-template-modal";
import { CampaignTemplateSummaryModal } from "containers/admin/clients/client/campaign-templates-page/campaign-template-summary-modal";
import CampaignTemplatesContextMenu from "containers/admin/clients/client/campaign-templates-page/campaign-templates-context-menu";
import FiltersOnCampaignTemplatesTable from "containers/admin/clients/client/campaign-templates-page/filters-on-campaign-templates-table";
import { useArchiveCampaignTemplateModal } from "containers/admin/clients/client/campaign-templates-page/use-archive-campaign-template-modal";

import { createCampaignTemplatesTableColumns } from "containers/admin/clients/client/campaign-templates-page/campaign-templates-table-columns";
import { CampaignTemplatesSelectionContext } from "containers/admin/clients/client/campaign-templates-page/campaign-templates-selection-context";
import { useCampaignTemplateSummaries } from "hooks/api/use-campaign-templates";
import type { CampaignTemplateListQueryParams } from "hooks/api/query-keys";
import { useUserLayout, useDebouncedUpdateUserLayout } from "hooks/api/use-user-layout";
import { Route as AppRoute, buildPath } from "utilities/app-routes";

import type {
  CampaignTemplate,
  FiltersToApplyOnCampaignTemplatesPage,
} from "containers/admin/clients/client/campaign-templates-page/campaign-templates-page.types";
import type { ColumnOption } from "types/table";
import type { CampaignTemplateDTO, CampaignTemplateWithDataDTO } from "generated/schemas";

const TABLE_CONFIG_KEY = "campaignTemplates";

const SEARCH_FIELD_SX: SxProps<Theme> = { width: 400 };

const PRIMARY_COLUMN_IDS = ["checkbox", "name", "actions"];

const DEFAULT_CAMPAIGN_TEMPLATE_FILTERS_TO_APPLY: FiltersToApplyOnCampaignTemplatesPage = {
  programType: [],
};

const CAMPAIGN_TEMPLATE_FILTER_ID_TO_TITLE: Record<string, string> = {
  programType: "Program type",
};

const CAMPAIGN_TEMPLATES_BREADCRUMBS = [
  { label: "Admin workspace", active: false as const },
  { label: "Campaign briefs", active: true },
];

const SORT_COLUMN_TO_FIELD: Record<string, string> = {
  name: "name",
  program: "programType",
  lastUpdated: "lastModifiedDate",
};

const DEFAULT_SORT = ["lastModifiedDate,desc"];

const mapCampaignTemplate = (template: CampaignTemplateDTO): CampaignTemplate | undefined => {
  if (!template.id || !template.name || !template.programType) {
    return undefined;
  }

  return {
    id: template.id,
    name: template.name,
    strategy: template.strategy,
    lastUpdated: template.lastModifiedDate ?? template.createdDate ?? "",
    program: template.programType,
  };
};

const CampaignTemplatesPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [filtersToApply, setFiltersToApply] = useState<FiltersToApplyOnCampaignTemplatesPage>(
    DEFAULT_CAMPAIGN_TEMPLATE_FILTERS_TO_APPLY,
  );
  const [sortingParams, setSortingParams] = useState<SortingState>([
    { id: "lastUpdated", desc: true },
  ]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [isCreateTemplateModalOpen, setIsCreateTemplateModalOpen] = useState(false);
  const [summaryTemplateId, setSummaryTemplateId] = useState<string | null>(null);

  const sortQueryParams = useMemo(
    () =>
      sortingParams
        .map(({ id, desc }) => {
          const field = SORT_COLUMN_TO_FIELD[id];

          return field ? `${field},${desc ? "desc" : "asc"}` : null;
        })
        .filter((value): value is string => value !== null),
    [sortingParams],
  );

  const listQueryParams = useMemo(
    (): CampaignTemplateListQueryParams => ({
      q: searchTerm.trim() || undefined,
      sort: sortQueryParams.length ? sortQueryParams : DEFAULT_SORT,
      filters: {
        programType: filtersToApply.programType,
      },
    }),
    [filtersToApply.programType, searchTerm, sortQueryParams],
  );

  const {
    data: campaignTemplates = [],
    isLoading,
    isFetching,
  } = useCampaignTemplateSummaries(listQueryParams, { placeholderData: keepPreviousData });
  const { data: tableColumnsConfig = {} } = useUserLayout();
  const { toggleColumnVisibility } = useDebouncedUpdateUserLayout();

  const hiddenColumnIds = useMemo(
    () => tableColumnsConfig[TABLE_CONFIG_KEY]?.hidden ?? [],
    [tableColumnsConfig],
  );

  const clearSelection = useCallback(() => {
    setSelectedTemplateIds([]);
  }, []);

  const handleRemoveSelectedItem = useCallback((id: string) => {
    setSelectedTemplateIds((current) => current.filter((templateId) => templateId !== id));
  }, []);

  const { openArchiveModal, archiveModalMarkup } = useArchiveCampaignTemplateModal({
    onSuccess: clearSelection,
    onRemoveSelectedItem: handleRemoveSelectedItem,
  });

  const tableData = useMemo(
    () => campaignTemplates.flatMap((template) => mapCampaignTemplate(template) ?? []),
    [campaignTemplates],
  );

  const selectedTemplates = useMemo(
    () => tableData.filter((template) => selectedTemplateIds.includes(template.id)),
    [selectedTemplateIds, tableData],
  );

  const isCurrentPageAllSelected = useMemo(
    () =>
      tableData.length > 0 &&
      tableData.every((template) => selectedTemplateIds.includes(template.id)),
    [selectedTemplateIds, tableData],
  );

  const handleArchiveTemplate = useCallback(
    (template: CampaignTemplate) => {
      openArchiveModal(template);
    },
    [openArchiveModal],
  );

  const handleArchiveTemplates = useCallback(
    (templates: CampaignTemplate[]) => {
      openArchiveModal(templates);
    },
    [openArchiveModal],
  );

  const handleTemplateClick = useCallback(
    (templateId: string) => {
      navigate(buildPath(AppRoute.admin, AppRoute.campaignTemplates, templateId));
    },
    [navigate],
  );

  const handleTemplateCreated = (template: CampaignTemplateWithDataDTO) => {
    setIsCreateTemplateModalOpen(false);

    if (template.id) {
      navigate(buildPath(AppRoute.admin, AppRoute.campaignTemplates, template.id), {
        state: { openInEditMode: true },
      });
    }
  };

  const allColumns = useMemo(
    () =>
      createCampaignTemplatesTableColumns({
        onTemplateClick: handleTemplateClick,
        onArchiveTemplate: handleArchiveTemplate,
        onGenerateSummary: setSummaryTemplateId,
      }),
    [handleArchiveTemplate, handleTemplateClick],
  );

  const selection = useMemo(
    () => ({
      selectedTemplateIds,
      setSelectedTemplateIds,
      displayedTemplates: tableData,
      isCurrentPageAllSelected,
    }),
    [selectedTemplateIds, tableData, isCurrentPageAllSelected],
  );

  const columnOptions = useMemo<Array<ColumnOption>>(
    () =>
      allColumns
        .filter((column) => typeof column.header === "string" && column.id !== "actions")
        .map((column) => ({ id: column.id ?? "", label: column.header as string })),
    [allColumns],
  );

  const hiddenColumns = useMemo(() => new Set(hiddenColumnIds), [hiddenColumnIds]);
  const columns = useMemo(
    () => allColumns.filter((column) => !hiddenColumns.has(column.id ?? "")),
    [allColumns, hiddenColumns],
  );

  const pinPrimaryColumnLeft = shouldPinPrimaryColumnLeft(columns, PRIMARY_COLUMN_IDS);

  const handleSearchTermChange = (nextSearchTerm: string) => {
    setSearchTerm(nextSearchTerm);
  };

  const handleToggleColumn = useCallback(
    (columnId: string) => toggleColumnVisibility(TABLE_CONFIG_KEY, columnId),
    [toggleColumnVisibility],
  );

  const actionSlot = (
    <div className="flex items-center gap-2.5">
      <CampaignTemplatesContextMenu
        allSelectedAmount={selectedTemplates.length}
        totalTemplates={tableData.length}
        selectedTemplates={selectedTemplates}
        onArchiveTemplates={handleArchiveTemplates}
      />
      {!selectedTemplates.length && (
        <ControlBar variant="inset">
          <Button size="large" onClick={() => setIsCreateTemplateModalOpen(true)}>
            Create
          </Button>
        </ControlBar>
      )}
    </div>
  );

  return (
    <FillHeightPage>
      <Breadcrumbs breadcrumbs={CAMPAIGN_TEMPLATES_BREADCRUMBS} />
      <TableContainer fillHeight>
        <TableSearchAndFilters
          setCurrentSearchTerm={handleSearchTermChange}
          placeholder="Search"
          searchSx={SEARCH_FIELD_SX}
          filtersToApply={filtersToApply}
          setFiltersToApply={setFiltersToApply}
          defaultFiltersToApply={DEFAULT_CAMPAIGN_TEMPLATE_FILTERS_TO_APPLY}
          filterIdToTitle={CAMPAIGN_TEMPLATE_FILTER_ID_TO_TITLE}
          tableFilters={
            <>
              <FiltersOnCampaignTemplatesTable
                filtersToApply={filtersToApply}
                setFiltersToApply={setFiltersToApply}
              />
              <ColumnOptions
                tableConfigKey={TABLE_CONFIG_KEY}
                tableColumnsConfig={tableColumnsConfig}
                columnOptions={columnOptions}
                onToggleColumn={handleToggleColumn}
              />
            </>
          }
          actionSlot={actionSlot}
        />

        <TableFillHeightBody>
          <TableLoader isLoading={isLoading} fullScreen />

          {!isLoading && (
            <CampaignTemplatesSelectionContext value={selection}>
              <DataGrid
                columns={columns}
                data={tableData}
                isLoading={isFetching}
                manualSorting={true}
                showingRecordsIsOn={true}
                viewByAmountSelectIsOn={true}
                pagination={false}
                totalRecords={tableData.length}
                pageSize={tableData.length}
                pagesAmount={0}
                pageIndex={0}
                sortingParams={sortingParams}
                setSortingParams={setSortingParams}
                pinPrimaryColumnLeft={pinPrimaryColumnLeft}
                fillHeight
              />
            </CampaignTemplatesSelectionContext>
          )}
        </TableFillHeightBody>
      </TableContainer>
      {isCreateTemplateModalOpen && (
        <CreateCampaignTemplateModal
          onClose={() => setIsCreateTemplateModalOpen(false)}
          onCreated={handleTemplateCreated}
        />
      )}
      {summaryTemplateId && (
        <CampaignTemplateSummaryModal
          source={{ type: "template", templateId: summaryTemplateId }}
          onClose={() => setSummaryTemplateId(null)}
        />
      )}
      {archiveModalMarkup}
    </FillHeightPage>
  );
};

export default CampaignTemplatesPage;
